import * as React from "react"
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from "lucide-react"
import * as SelectPrimitive from "@radix-ui/react-select"

import cn from "@/v2-ui/utils/utils.cn"

const Select = SelectPrimitive.Root

const SelectGroup = SelectPrimitive.Group

const SelectValue = SelectPrimitive.Value

const SelectPortal = SelectPrimitive.Portal

const SelectTrigger = ({
  children,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Trigger>) => (
  <SelectPrimitive.Trigger {...props}>
    {children}
    <SelectPrimitive.Icon asChild>
      <ChevronDownIcon className="size-4 text-muted-foreground pointer-events-none" />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
)
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName

const SelectScrollUpButton = (
  props: React.ComponentProps<typeof SelectPrimitive.ScrollUpButton>,
) => (
  <SelectPrimitive.ScrollUpButton {...props}>
    <ChevronUpIcon />
  </SelectPrimitive.ScrollUpButton>
)
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName

const SelectScrollDownButton = (
  props: React.ComponentProps<typeof SelectPrimitive.ScrollDownButton>,
) => (
  <SelectPrimitive.ScrollDownButton {...props}>
    <ChevronDownIcon />
  </SelectPrimitive.ScrollDownButton>
)
SelectScrollDownButton.displayName =
  SelectPrimitive.ScrollDownButton.displayName

const SelectContent = ({
  className,
  children,
  position = "popper",
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Content>) => (
  <SelectPrimitive.Content
    className={cn(
      "",
      position === "popper" &&
        "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
      className,
    )}
    position={position}
    {...props}
  >
    <SelectScrollUpButton />
    <SelectPrimitive.Viewport
      className={cn(
        "p-1",
        position === "popper" &&
          "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]",
      )}
    >
      {children}
    </SelectPrimitive.Viewport>
    <SelectScrollDownButton />
  </SelectPrimitive.Content>
)
SelectContent.displayName = SelectPrimitive.Content.displayName

const SelectLabel = (
  props: React.ComponentProps<typeof SelectPrimitive.Label>,
) => <SelectPrimitive.Label {...props} />
SelectLabel.displayName = SelectPrimitive.Label.displayName

export type SelectItemBaseProps = React.ComponentProps<
  typeof SelectPrimitive.Item
> & {
  /**
   * A second, quieter line under the label, shown in the open list only:
   * it sits outside ItemText, so the closed trigger keeps showing the
   * label alone. For options that need a sentence to be chosen between.
   */
  description?: React.ReactNode
}

const SelectItem = ({
  children,
  description,
  ...props
}: SelectItemBaseProps) => (
  <SelectPrimitive.Item {...props}>
    <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
      <SelectPrimitive.ItemIndicator>
        <CheckIcon className="h-4 w-4" />
      </SelectPrimitive.ItemIndicator>
    </span>
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    {description != null && (
      <span className="block text-xs leading-snug text-muted-foreground">
        {description}
      </span>
    )}
  </SelectPrimitive.Item>
)
SelectItem.displayName = SelectPrimitive.Item.displayName

const SelectSeparator = (
  props: React.ComponentProps<typeof SelectPrimitive.Separator>,
) => <SelectPrimitive.Separator {...props} />
SelectSeparator.displayName = SelectPrimitive.Separator.displayName

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
  SelectPortal,
}
